import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import produce from 'immer'
import { Button } from 'primereact/button'

import { useToastContext } from '../../../../../../../../contexts'
import {
  DeliveryForecast,
  DeliveryForecastItemSplit,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../../../../../../data/models'
import { getHandleableError } from '../../../../../../../../utils/errors/isXiaError'
import { useConfigContext } from '../../../../../../../ui-config'
import {
  requestPatchDeliveryForecastsById,
  requestPutSplitItem,
  useAllDeliveryForecastItemSplits,
} from '../../../../../../data'
import { rejectItemSplits } from '../../../../../../scripts'
import { isRejectButtonDisabled } from './isRejectButtonDisabled'

type Props = {
  selectedItems: DeliveryForecastItemSplit[] | null | undefined
  setSelectedItems: (params: any) => void
  rowData?: DeliveryForecastItemSplit
  deliveryForecastId: string
  itemId: string
  iconOnly?: boolean
}

export const RejectSplitItems: React.FC<Props> = ({
  selectedItems,
  setSelectedItems,
  rowData,
  deliveryForecastId,
  itemId,
  iconOnly,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()
  const { data: itemSplits } = useAllDeliveryForecastItemSplits(
    deliveryForecastId,
    itemId,
  )
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const [rowDataInArray, setRowDataInArray] = useState<
    DeliveryForecastItemSplit[] | null
  >(null)

  useEffect(() => {
    if (rowData) {
      setRowDataInArray([rowData])
    }
  }, [rowData])

  function onSubmit() {
    // deactivate the buttons
    setIsRequestRunning(true)

    try {
      // submit form
      if (rowData) {
        rejectItemSplits(
          deliveryForecastId,
          itemId,
          rowDataInArray!,
          itemSplits!,
        )
      } else {
        rejectItemSplits(
          deliveryForecastId,
          itemId,
          selectedItems!,
          itemSplits!,
        )
      }

      // deselect the items in the table
      setSelectedItems([])

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'deliveryForecast.deliveryScheduleDetails.splits.table.reject.messages.success.summary',
        ),
        detail: rowData
          ? undefined
          : t(
              'deliveryForecast.deliveryScheduleDetails.splits.table.reject.messages.success.detail',
              {
                count: selectedItems?.length,
              },
            ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'deliveryForecast.deliveryScheduleDetails.splits.table.reject.messages.failure.summary',
          {
            count: rowData ? rowDataInArray?.length : selectedItems?.length,
          },
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const submitForm = () => {
    selectedItems?.forEach(item => {
      // Change ConfirmationCode to modified because we rejected one
      const newConfirmationCode: DeliveryForecast['ConfirmationCode'] =
        'MODIFIED'
      const patchData: PatchRequest<
        WebEdiDeliveryForecastDto<DeliveryForecast>
      > = [
        {
          op: 'replace',
          path: '/Payload/ConfirmationCode',
          value: newConfirmationCode,
        },
      ]

      requestPatchDeliveryForecastsById(deliveryForecastId, patchData)

      // Make a copy of the item
      const newItemSplit = produce(item, draft => {
        draft.Status = 'REJECTED'
      })

      // Put for splitItem
      const response = requestPutSplitItem(
        deliveryForecastId,
        itemId,
        // @ts-ignore
        item.Id,
        newItemSplit,
      )

      // Return response header
      return response
    })
  }

  return (
    <>
      {iconOnly ? (
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-text"
          onClick={onSubmit}
          disabled={isRejectButtonDisabled(rowDataInArray) || false}
          loading={isRequestRunning}
          visible={config.deliveryForecast.rejectSplitItem.enabled !== false}
        />
      ) : (
        <Button
          label={t('common.buttons.reject')}
          icon="pi pi-times"
          className="p-button-outlined mb-2"
          onClick={onSubmit}
          disabled={isRejectButtonDisabled(selectedItems)}
          loading={isRequestRunning}
          visible={config.deliveryForecast.rejectSplitItem.enabled !== false}
        />
      )}
    </>
  )
}
